import { bindCanvasInvestigation, closeTraceCanvas, openCaseCanvas } from "../lib/traceCanvasStore.js";
// WorkspaceInventory: the Workspaces page as the team's investigations. Each
// row is what is being investigated, by whom, when it was opened and when it was
// last worked on, latest activity first; filtered by status (the open ones at
// first), a search, and a range of either date. A row opens
// its work: the board this browser keeps for it, else a new local board bound to the shared investigation.
import { useCallback, useMemo, useState } from "react";
import DateRangePicker from "./DateRangePicker.jsx";
import ScrollArea from "./ScrollArea.jsx";
import SearchInput from "./ui/SearchInput.jsx";
import Select from "./ui/Select.jsx";
import { SkeletonBar, SkeletonRows } from "./ui/Skeleton.jsx";
import Tabs from "./ui/Tabs.jsx";
import { getFindingInvestigation } from "../lib/alertTriageClient.js";
import { showWorkspaceInDeck, startWorkspaceInDeck } from "../lib/deckRequests.js";
import { requestFreshChat } from "./CanvasChat.jsx";
import { buttonClass } from "./ui/Button.jsx";
import { ownerLabel } from "../lib/findingsConsole.js";
import { PERMISSIONS } from "../lib/permissionKeys.js";
import { useHasAnyPermission } from "../lib/permissions.js";
import { bytesBase64ToUuid } from "../lib/reviewClient.js";
import { rangeSpan } from "../lib/shell.js";
import { caseCanvasId, MAX_BOARD_SESSIONS, reopenTraceCanvas, saveTraceCanvas, traceCanvasSnapshot, useTraceCanvases } from "../lib/traceCanvasStore.js";
import { useConsoleInvestigations } from "../lib/useConsoleInvestigations.js";
import { useCurrentUser } from "../lib/userInfo.js";
import { boardOf, DATE_FIELDS, inventoryView, listed, mergeReads, onDate, OPEN_STATUSES, setInventoryView, STATUSES, STATUS_LABEL, statusCounts, statusWords, when } from "../lib/workspaceInventory.js";

// The reader as the investigation API names users, a UUID: /v1/me may send
// the id's bytes base64-encoded.
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const asUserId = (id) => (!id ? null : UUID.test(id) ? id.toLowerCase() : bytesBase64ToUuid(id) || id);

const COLS = "grid-cols-[minmax(240px,1fr)_minmax(140px,200px)_104px_128px]";
const COLUMNS = [["title", "Investigation"], ["owner", "Owner"], ["opened", "Opened"], ["activity", "Last activity"]];

// A person as a small disc with their initial: the reader's in the accent,
// a teammate's in gray, nobody's an empty outline.
function Disc({ person }) {
  const tone = !person.id ? "border border-dashed border-[color:var(--line-strong)]"
    : person.mine ? "bg-[color:var(--green-soft)] text-[color:var(--green)]" : "bg-[color:var(--gray-fill)] text-[color:var(--muted)]";
  return <span aria-hidden="true" className={`grid h-[18px] w-[18px] shrink-0 place-items-center rounded-full text-[10px] font-semibold leading-none ${tone}`}>{person.letter}</span>;
}

export default function WorkspaceInventory() {
  const user = useCurrentUser();
  const me = asUserId(user?.userId);
  const canWrite = useHasAnyPermission([PERMISSIONS.ALERT_TRIAGE_WRITE, PERMISSIONS.USERS_MANAGE]);
  const [view, setView] = useState(() => ({ ...inventoryView }));
  // The open investigations and the closed ones are read apart, side by
  // side, so the list the page opens on never waits on an organization's
  // history. A closed status counts once its read lands, never before.
  const openInvs = useConsoleInvestigations({ enabled: true, canWrite, status: "open" });
  const closedInvs = useConsoleInvestigations({ enabled: true, status: "closed" });
  const closedRead = closedInvs.status === "done";
  // A first closed read that failed (a later one keeps the last it had).
  const closedFailed = closedInvs.status === "error";
  const byId = useMemo(() => mergeReads(openInvs.byId, closedRead ? closedInvs.byId : null), [openInvs.byId, closedRead, closedInvs.byId]);
  const invs = { ...openInvs, byId, capped: openInvs.capped || closedInvs.capped };
  const change = (patch) => {
    setInventoryView(patch);
    setView((v) => ({ ...v, ...patch }));
  };
  const [opening, setOpening] = useState(null);
  const [failed, setFailed] = useState(null);
  const workspaces = useTraceCanvases();
  const now = useMemo(() => Date.now(), [invs.byId]);

  const nameOf = useCallback((id) => ownerLabel(id, invs.users, me), [invs.users, me]);
  const personOf = useCallback((id) => {
    if (!id) return { id: null, name: "Unassigned", letter: "" };
    const email = id === me ? user?.email || "" : invs.users.get(id) || "";
    return { id, name: nameOf(id), letter: email.charAt(0).toUpperCase(), mine: id === me };
  }, [invs.users, me, nameOf, user?.email]);
  const filter = useMemo(() => {
    const span = view.range ? rangeSpan(view.range) : null;
    return { opened: view.dateField === "opened" ? span : null, activity: view.dateField === "activity" ? span : null, query: view.query, nameOf };
  }, [view.range, view.dateField, view.query, nameOf]);
  const list = useMemo(() => listed(invs.byId, { ...filter, statuses: view.statuses, by: view.by, reverse: view.reverse }), [invs.byId, filter, view.statuses, view.by, view.reverse]);
  const counts = useMemo(() => statusCounts(invs.byId, filter), [invs.byId, filter]);

  const sortBy = (by) => change({ by, reverse: view.by === by ? !view.reverse : false });
  const toggleStatus = (status) => change({ statuses: view.statuses.includes(status) ? view.statuses.filter((s) => s !== status) : [...view.statuses, status] });

  // An investigation's members say which board is its, read when it is
  // opened. With none kept in this browser, its findings (those not set
  // aside, else all) are laid out on a new one, anchored on the first's
  // session; one with no findings opens on its question (its summary, else
  // its title).
  const open = useCallback(async (inv) => {
    if (opening) return;
    setOpening(inv.incidentId);
    setFailed(null);
    try {
      const got = await getFindingInvestigation(inv.incidentId);
      const boards = workspaces.filter((w) => w.id.startsWith("case")).map((w) => ({ id: w.id, investigationId: traceCanvasSnapshot(w.id).investigationId, findings: traceCanvasSnapshot(w.id).findings.map((f) => f.id) }));
      const kept = boardOf(got.findings, boards, inv.incidentId);
      const live = got.findings.filter((f) => !f.setAsideReason);
      const members = live.length ? live : got.findings;
      const board = kept || openCaseCanvas({
        id: caseCanvasId("investigation", inv.incidentId),
        title: inv.title || "Investigation",
        question: members.length ? "" : got.summary || got.title || inv.title || "",
        anchor: members[0]?.trace_id || "",
        sessions: [...new Set(members.map((f) => f.trace_id))].slice(0, MAX_BOARD_SESSIONS).map((id) => ({ id })),
        findings: members.map((f) => ({ id: f.finding_id, session: f.trace_id, title: "" })),
        open: false,
      });
      if (!board) return setFailed(inv.incidentId);
      bindCanvasInvestigation(board, inv.incidentId);
      if (!kept) saveTraceCanvas(board);
      if (!showWorkspaceInDeck(board)) reopenTraceCanvas(board);
    } catch {
      setFailed(inv.incidentId);
    } finally {
      setOpening(null);
    }
  }, [opening, workspaces]);

  const loading = invs.status === "loading";
  const sort = { by: view.by, reverse: view.reverse };
  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col" data-workspace-inventory="">
      <header className="grid shrink-0 gap-[10px] pb-[14px] pr-[16px] pt-[7px]">
        {/* The rail names the page; the heading stays for assistive tech. */}
        <h1 className="sr-only">Investigations</h1>
        <div>
          <button
            type="button"
            onClick={() => { requestFreshChat(); closeTraceCanvas(); startWorkspaceInDeck(); }}
            className={buttonClass("secondary")}
            data-inventory-new=""
          >
            + New investigation
          </button>
        </div>
        <div className="flex flex-wrap items-center gap-x-[16px] gap-y-[8px]">
          {/* Several statuses can be picked at once; none picked lists every status. */}
          <Tabs
            mode="toggle"
            className="mr-auto"
            label="Status"
            value={view.statuses}
            onChange={toggleStatus}
            itemAttrs={(item) => ({ "data-inventory-status": item.id })}
            items={STATUSES.map((s) => ({
              id: s,
              label: (
                <span className="flex items-center gap-[7px]">
                  {STATUS_LABEL[s]}
                  {!closedRead && closedFailed && !OPEN_STATUSES.includes(s) ? null
                    : loading || (!closedRead && !OPEN_STATUSES.includes(s))
                    ? <SkeletonBar className="w-[14px]" />
                    : <span className="text-[11.5px] tabular-nums text-[color:var(--muted-soft)]">{counts[s]}{invs.capped ? "+" : ""}</span>}
                </span>
              ),
            }))}
          />
          <div className="min-w-[260px] max-w-[560px] flex-[1_1_360px]">
            <SearchInput size="sm" value={view.query} onChange={(query) => change({ query })} placeholder="Search" label="Search investigations" className="h-[32px] w-full" data-inventory-query="" />
          </div>
          <span className="w-[140px] shrink-0" data-inventory-date-field={view.dateField}>
            <Select value={view.dateField} onChange={(dateField) => change({ dateField })} options={Object.entries(DATE_FIELDS).map(([value, label]) => ({ value, label }))} ariaLabel="Date the range applies to" />
          </span>
          <span className="w-[160px] shrink-0" data-inventory-range="">
            <DateRangePicker anyTime range={view.range} onRangeChange={(range) => change({ range })} />
          </span>
        </div>
      </header>
      {invs.capped ? <p className="m-0 shrink-0 pb-[8px] text-[12px] text-[color:var(--muted)]" data-inventory-capped="">Showing the newest 1,000 {openInvs.capped && closedInvs.capped ? "open and the newest 1,000 closed" : openInvs.capped ? "open" : "closed"} investigations.</p> : null}
      {!loading && !closedRead && (!view.statuses.length || view.statuses.some((st) => !OPEN_STATUSES.includes(st))) ? (
        closedFailed
          ? <p role="alert" className="m-0 shrink-0 pb-[8px] text-[12.5px] text-[color:var(--severity-critical)]" data-inventory-closed-failed="">The closed investigations could not be read. Reload to try again.</p>
          : <p role="status" className="m-0 shrink-0 pb-[8px] text-[12px] text-[color:var(--muted)]" data-inventory-reading-closed="">Reading the closed investigations…</p>
      ) : null}
      {failed ? <p role="alert" className="m-0 shrink-0 pb-[8px] text-[12.5px] text-[color:var(--severity-critical)]">That investigation could not be opened. Try again.</p> : null}
      <ScrollArea className="min-h-0 flex-1" contentClassName="pb-[96px]">
        <div role="table" aria-label="Investigations" aria-busy={loading ? "true" : undefined}>
          <div role="row" className={`sticky top-0 z-[1] grid ${COLS} items-end gap-x-[16px] border-b border-[color:var(--line)] bg-[color:var(--bg)] pb-[6px] pl-[12px] pr-[16px]`}>
            {COLUMNS.map(([by, label]) => (
              <span key={by} role="columnheader" aria-sort={sort.by === by ? (sort.reverse ? "ascending" : "descending") : "none"}>
                <button type="button" onClick={() => sortBy(by)} className={`flex items-center gap-[5px] text-[12px] ${sort.by === by ? "text-[color:var(--ink)]" : "text-[color:var(--muted)] hover:text-[color:var(--ink)]"}`} data-inventory-sort={by}>
                  {label}
                  {sort.by === by ? <span aria-hidden="true" className="text-[10px]">{sort.reverse ? "↑" : "↓"}</span> : null}
                </button>
              </span>
            ))}
          </div>
          {invs.status === "error" ? <p role="alert" className="m-0 py-[24px] pl-[12px] text-[13px] text-[color:var(--severity-critical)]">The investigations could not be read. Reload to try again.</p>
            : loading ? <SkeletonRows rows={8} cells={["w-[60%]", "w-[55%]", "w-[64px]", "w-[72px]"]} rowClassName={`grid h-[42px] ${COLS} items-center gap-x-[16px] pl-[12px] pr-[16px]`} label="Loading investigations…" />
              : !list.length ? <p className="m-0 py-[24px] pl-[12px] text-[13px] text-[color:var(--muted)]" data-inventory-empty="">{invs.byId.size ? "No investigations match these filters." : "No investigations yet."}</p>
                : list.map((inv) => {
                  const done = inv.status === "closed";
                  const owner = personOf(inv.ownerUserId);
                  return (
                    <button
                      key={inv.incidentId}
                      type="button"
                      role="row"
                      onClick={() => open(inv)}
                      aria-busy={opening === inv.incidentId ? "true" : undefined}
                      className={`grid min-h-[42px] w-full ${COLS} items-center gap-x-[16px] border-b border-[color:var(--line)] py-[8px] pl-[12px] pr-[16px] text-left hover:bg-[color:var(--gray-fill)]`}
                      data-inventory-row={inv.incidentId}
                    >
                      <span role="cell" className="flex min-w-0 items-baseline gap-[10px]">
                        <span className={`truncate text-[13px] ${done ? "text-[color:var(--muted)]" : "text-[color:var(--ink)]"}`}>{inv.title || "Untitled investigation"}</span>
                        {done ? <span className="shrink-0 text-[12px] text-[color:var(--muted)]">{statusWords(inv)}</span> : null}
                      </span>
                      <span role="cell" className="flex min-w-0 items-center gap-[7px]">
                        <Disc person={owner} />
                        <span className={`truncate text-[12.5px] ${owner.id ? "text-[color:var(--ink)]" : "text-[color:var(--muted)]"}`}>{owner.name}</span>
                      </span>
                      <span role="cell" className="text-[12.5px] tabular-nums text-[color:var(--muted)]">{onDate(inv.createdAtMs, now)}</span>
                      <span role="cell" className="text-[12.5px] tabular-nums text-[color:var(--ink)]">{when(inv.updatedAtMs, now)}</span>
                    </button>
                  );
                })}
        </div>
      </ScrollArea>
    </div>
  );
}
